/**
 * Engagement Adapter
 * Transforms between API representations (Swagger) and UI representations (FormState)
 * Part of the boundary layer between API and UI domains
 * 
 * Converts:
 * - API responses → UI FormState (for loading)
 * - UI FormState → JSON Patch operations (for saving)
 */

import type { PatchOperation } from '@/types/api/engagement-api-client'
import type { 
  Engagement, 
  EngagementResponse, 
  AvailableActions,
  Worker,
  RemovedWorker,
  EngagementReferences
} from '@/types/api/engagement'
import type { EngagementVersionContext, FormState } from '@/types/ui/engagement'
import { ENGAGEMENT_REQUEST_TYPE, ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE } from '@/lib/constants/engagement-codes'
import { TIMESHEET_APPROVAL_TYPE, EXPENSE_METHODOLOGY } from '@/lib/constants/work-order-codes'
import { 
  WorkerFormData,
  WORKER_TYPE,
  WorkerFormLegacySyncStatus
} from '@/lib/schemas'
import { isWorkerNew } from '@/lib/utils/worker-utils'
import { BASE_RATE_TYPE_CODE } from '@/lib/utils/worker-rate-slots'
import { parseDateOnly } from '@/lib/utils'

/**
 * Convert a Date object to YYYY-MM-DD string in local timezone
 * This ensures the date doesn't shift when converting back to API format
 */
export function formatDateOnly(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0') // month is 0-indexed
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Converts empty or whitespace-only strings to null for API
 * Form fields store empty string, but API expects null
 */
function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed || null
}

/**
 * Converts 0 to null for ID fields
 * Form stores 0 for unselected IDs, but API expects null
 */
function unselectedIdToNull(value: number | null | undefined): number | null {
  return value || null
}


// Stubbed values
export const STUB_RECORD_ID = 1
export const STUB_VERSION = 1

/**
 * Loads engagement data using the versions endpoint (internal management pattern)
 * Gets all versions, finds the latest, and follows the self link to load the specific version
 * Uses: GET /api/engagement/{recordId}/versions -> GET {self_link}
 */


/**
 * Transforms API engagement response to form state (including available actions)
 * 
 * ID Loading Rule: API may return null for unselected IDs, convert to 0 for form state
 * Form consistently uses 0 for unselected IDs, then || null converts back for API
 */
export function apiToFormState(engagementResponse: EngagementResponse, previousWorkerIds?: number[]): FormState {
  const engagement = engagementResponse.data
  const actionContext = createActionContext(
    engagement,
    engagementResponse.links || {},
    // Fail-closed for display = hide, so default false. This is intentionally the OPPOSITE of the
    // validation path (use-engagement-forms.ts uses `?? true`, where fail-closed = require). Don't
    // unify them. The backend always sends this flag, so the fallback only matters defensively.
    engagementResponse.canViewWorkerPayRates ?? false,
    engagementResponse.references ?? { profiles: {}, organizations: {}, engagements: {} },
    engagementResponse.canEditEngagementSource ?? false
  )
  const isBillableDisallowed = engagement.coreInfo.billingTypeCode === ENGAGEMENT_BILLING_TYPE.MILESTONE
    || engagement.coreInfo.engagementTypeCode === ENGAGEMENT_TYPE.INTERNAL

  return {
    // Core section
    coreData: {
      engagementName: engagement.coreInfo.name || '',
      engagementCode: engagement.coreInfo.code || '',
      engagementType: engagement.coreInfo.engagementTypeCode || '', 
      requestType: engagement.coreInfo.requestTypeCode || '',
      selectedClient: engagement.clientInfo.clientOrganizationId || 0,
      selectedContact: engagement.clientInfo.clientContactUserProfileId || 0,
      startDate: engagement.coreInfo.startDate ? parseDateOnly(engagement.coreInfo.startDate) : null,
      endDate: engagement.coreInfo.endDate ? parseDateOnly(engagement.coreInfo.endDate) : null,
      agreementSignedDate: engagement.coreInfo.agreementSignedDate ? parseDateOnly(engagement.coreInfo.agreementSignedDate) : null,
      isPreEngagement: engagement.coreInfo.isPreEngagement ?? false,
      closedDate: engagement.coreInfo.closedDate ? parseDateOnly(engagement.coreInfo.closedDate) : null,
      closedReason: engagement.coreInfo.closedReasonCode || '',
      selectedInternalCompany: engagement.coreInfo.internalCompanyOrganizationId || 0,
      selectedBranch: engagement.coreInfo.branchCode || '',
      selectedSalesPerson: engagement.coreInfo.salesPersonUserProfileId || 0,
      selectedEngagementLead: engagement.coreInfo.engagementLeadUserProfileId || 0,
      selectedProjectLead: engagement.coreInfo.projectLeadUserProfileId || 0,
      selectedClientExecutive: engagement.coreInfo.clientExecutiveUserProfileId || 0,
      selectedOriginalEngagement: engagement.coreInfo.engagementSourceId || 0
    },

    // Billing data
    billingData: {
      billingType: engagement.coreInfo.billingTypeCode || '',
      totalAmount: engagement.coreInfo.totalAmount || null,
      totalInternalBudget: engagement.coreInfo.totalInternalBudget || null,
      selectedCurrency: engagement.coreInfo.currencyCode || '',
      selectedSalesTax: engagement.coreInfo.salesTaxSubdivisionCode || '',
      // Preserve null for engagements created before this field existed (and bulk imports) — the
      // toggle is then resolved from the client role on load, rather than fabricating a value that a
      // subsequent save would persist (which could silently start taxing an exempt client). (GW-562)
      chargeSalesTax: engagement.coreInfo.chargeSalesTax ?? null,
    },
    
    // Workers section
    workers: [
      // Active workers
      ...(engagement.workers || []).map((worker) => transformApiWorkerToForm(worker, engagement.legacySync, false, previousWorkerIds)),
      // Removed workers  
      ...(engagement.removedWorkers || []).map((worker) => transformApiWorkerToForm(worker, engagement.legacySync, true, previousWorkerIds))
    ],
    
    // Timesheet section
    timesheetData: {
      selectedTimesheetCycle: engagement.timesheetInfo.timesheetCycleCode || 'WeeklyStartSunday',
      approvers: (engagement.timesheetInfo.approvers || [])
        .filter(approver => approver.userProfileId != null && approver.sequence != null)
        .map((approver) => ({
          userProfileId: approver.userProfileId!,
          sequence: approver.sequence!
        })),
      selectedApprovalSequence: engagement.timesheetInfo.timesheetApprovalFlowCode || '',
      expenseMethodologyCode: engagement.expenseInfo?.expenseMethodologyCode || EXPENSE_METHODOLOGY.NO_EXPENSE,
      expenseApprovers: (engagement.expenseInfo?.approvers || [])
        .filter(approver => approver.userProfileId != null && approver.sequence != null)
        .map((approver) => ({
          userProfileId: approver.userProfileId!,
          sequence: approver.sequence!
        }))
    },
    
    // Tasks section - force billable false for milestone/internal to prevent stale API data blocking validation
    tasks: (engagement.projects || []).map((project) => ({
      id: project.id,
      name: project.taskName || '',
      billable: isBillableDisallowed ? false : (project.billable || false),
      inactive: project.inactive || false,
      isUtilized: project.isUtilized ?? true,
    })),
    
    // Invoice section
    invoiceData: {
      selectedAlternateBill: engagement.invoiceInfo.alternateBillCode ?? null,
      selectedBillingFrequency: engagement.invoiceInfo.billingFrequencyCode || '',
      selectedPaymentTerms: engagement.invoiceInfo.paymentTermsCode || '',
      selectedInvoiceTemplate: engagement.invoiceInfo.invoiceTemplateCode || '',
      selectedInvoiceRecipient: engagement.invoiceInfo.invoiceRecipientUserProfileId || 0, // Store actual ID
      selectedDeliveryMethod: engagement.invoiceInfo.invoiceDeliveryMethodCode || '',
      includeTimesheet: engagement.invoiceInfo.includeTimesheet === true,
      // Convert API string[] to form object[] for useFieldArray compatibility
      invoiceNotes: (engagement.invoiceInfo.invoiceNotes || []).map(note => ({ value: note })),
      ccEmails: engagement.invoiceInfo.invoiceCcEmails || []
    },
    
    // Milestones section
    milestones: (engagement.milestones || []).map((milestone, index) => ({
      id: index + 1, // Generate form ID
      startDate: milestone.startDate ? parseDateOnly(milestone.startDate) : null,
      endDate: milestone.endDate ? parseDateOnly(milestone.endDate) : null,
      status: milestone.statusCode || '',
      amount: milestone.amount || 0,
      internalBudget: milestone.internalBudget || 0,
      reference: milestone.milestoneReference || `MS-${String(index + 1).padStart(3, '0')}`,
      description: milestone.description || ''
    })),
    
    // Revenue Recognition section (transform API array to form Record)
    revenueRecognitionData: {
      periods: (engagement.revenueRecognition?.periods || []).reduce((acc, period) => {
        acc[period.startDate] = { 
          amount: period.amount,
          isLocked: period.isLocked ?? false
        }
        return acc
      }, {} as Record<string, { amount: number | null; isLocked: boolean }>)
    },
    
    // System metadata
    lastModifiedDateTime: engagement.lastModifiedDateTime ? new Date(engagement.lastModifiedDateTime) : null,
    
    // Available actions context (from HATEOAS links)
    actionContext
  }
}

/**
 * Creates action context from engagement data and available links
 */
export function createActionContext(
  engagement: Engagement,
  links: Record<string, any>,
  canViewWorkerPayRates: boolean = false,
  references: EngagementVersionContext['references'] = { profiles: {}, organizations: {}, engagements: {} },
  canEditEngagementSource: boolean = false
): EngagementVersionContext {
  const availableActions: AvailableActions = links || {}

  return {
    engagement,
    availableActions,
    canUpdate: hasAction(availableActions, 'update'),
    canDelete: hasAction(availableActions, 'delete'),
    canSave: hasAction(availableActions, 'save'),
    canSubmit: hasAction(availableActions, 'submit'),
    canSubmitForReview: hasAction(availableActions, 'submit-for-review'),
    canApprove: hasAction(availableActions, 'approve'),
    canDecline: hasAction(availableActions, 'decline'),
    canDiscard: hasAction(availableActions, 'discard'),
    canCreateChangeRequest: hasAction(availableActions, 'create-change-request'),
    canGenerateMilestoneTransaction: hasAction(availableActions, 'generate-milestone-transaction'),
    canRecall: hasAction(availableActions, 'recall'),
    canRecallToAccounting: hasAction(availableActions, 'recall-to-accounting'),
    canComplete: hasAction(availableActions, 'complete'),
    canReactivate: hasAction(availableActions, 'reactivate'),
    canLockRevenueRecognition: hasAction(availableActions, 'lockRevenueRecognition'),
    canViewWorkerPayRates,
    canEditEngagementSource,
    references
  }
}

/**
 * GW-323: overlays the references of a copied-from engagement beneath the current engagement's.
 *
 * Display names and worker types are resolved by id against `references`, which the API populates only
 * for the entities on the SAVED engagement. Straight after a copy-from-engagement the copied ids aren't
 * in there, and the global worker-profile list the UI falls back on is row-level filtered — so a
 * restricted user saw "Worker 12345" / "Unknown" until the next save refreshed references. The source
 * engagement resolved those very same ids under the same access rules, so its map is the right filler.
 *
 * Current entries win on conflict (fresher resolution for ids it already knows). `engagements` is taken
 * from current only: a copy doesn't bring the source's own Original Engagement across, so the source's
 * entry there would name an unrelated engagement.
 */
export function mergeCopiedReferences(
  current: EngagementReferences,
  copied: Pick<EngagementReferences, 'profiles' | 'organizations'> | null
): EngagementReferences {
  if (!copied) return current

  // Spread current first so members this merge has no opinion on (engagements, auditWorkers) carry
  // through untouched rather than being dropped by an explicit three-field rebuild.
  return {
    ...current,
    profiles: { ...copied.profiles, ...current.profiles },
    organizations: { ...copied.organizations, ...current.organizations }
  }
}

/**
 * Checks if a specific action is available in the links
 */
export function hasAction(availableActions: AvailableActions, actionName: string): boolean {
  return availableActions[actionName] !== undefined
}

/**
 * Determines worker status for display (business-friendly, hiding sync details)
 */
// Shown when a worker sync fails without a curated message (unexpected error, or a tenant-level failure
// with no per-worker result). Mirrors the backend's generic fallback so the wording is consistent.
const GENERIC_SYNC_ERROR = "This worker couldn't be synced. Please review the details or contact support."

function getWorkerSyncStatus(
  workerUserProfileId: number,
  legacySyncInfo?: any
): { status: WorkerFormLegacySyncStatus; error?: string | null } {
  // Don't show status if legacy sync hasn't been created
  if (!legacySyncInfo) {
    return { status: undefined }
  }

  const overallSyncStatus = legacySyncInfo.status
  const workerResult = legacySyncInfo?.workerResults?.find(
    (result: any) => result.workerUserProfileId === workerUserProfileId
  )
  // Only ever surface the curated user-facing message — never the raw errorMessage or tenant error, which
  // carry internal detail. Fall back to a generic message so an Error badge is always explainable.
  const error = workerResult?.userMessage ?? GENERIC_SYNC_ERROR

  // Check overall sync status first
  switch (overallSyncStatus) {
    case "Pending":
    case "InProgress":
      // Both are "Processing" from user perspective
      return { status: "Processing" }

    case "Failed":
      // Overall sync failed - show as Error
      return { status: "Error", error }

    case "Completed":
      return workerResult?.success ? { status: "Ready" } : { status: "Error", error }
    default:
      // Unknown status or undefined
      return { status: undefined }
  }
}

/**
 * The one rate a worker stored before the rate list existed, read off the flat fields, on the rate type
 * that is the only one such a worker ever used.
 * <p>
 * The backfill gives every worker their rates, so this is reached only by an engagement it has not run
 * against. Kept so such a worker renders rather than showing none, though their engagement is refused at
 * sync until the backfill has run.
 */
function liftedRateList(worker: Worker | RemovedWorker): WorkerFormData['payRates'] {
  if (!worker.timeBasedPay) {
    return []
  }

  return [{
    profileRateId: null,
    rateTypeCode: BASE_RATE_TYPE_CODE,
    payRate: worker.timeBasedPay.payRate ?? null,
    payRateUnitCode: worker.timeBasedPay.payRateUnitCode ?? null,
    description: null,
    scheduledPayRate: worker.scheduledPayRate ?? null,
    scheduledPayRateEffectiveDate: worker.scheduledPayRateEffectiveDate ?? null,
    // Billable exactly when there is something to charge, which is what legacy sync has always
    // written for a worker with no rate list.
    billable: worker.billRate != null,
  }]
}

/**
 * Transforms API worker to form worker
 */
export function transformApiWorkerToForm(worker: Worker | RemovedWorker, legacySyncInfo?: any, isRemoved: boolean = false, previousWorkerIds?: number[]): WorkerFormData {
  const workerId = worker.workerUserProfileId || 0
  const { status: workerStatus, error: workerSyncError } = getWorkerSyncStatus(workerId, legacySyncInfo)

  const formWorker: WorkerFormData = {
    workerUserProfileId: workerId,
    payRates: [],
    type: worker.t4SalariedPay ? WORKER_TYPE.T4_SALARIED : WORKER_TYPE.REGULAR,
    jobCategoryCode: worker.jobCategoryCode || '',
    jobFunctionCode: worker.jobFunctionCode || '',
    jobTitle: worker.jobTitle || '',
    lineOfBusinessCode: worker.lineOfBusinessCode || null,
    budgetedHours: worker.budgetedHoursPerDay || 0,
    scheduledBudgetedHours: (worker.scheduledBudgetedHours ?? []).map(entry => ({
      id: entry.id ?? null,
      effectiveDate: entry.effectiveDate,
      hoursPerDay: entry.hoursPerDay,
      appliedDate: entry.appliedDate ?? null,
      cancelledDate: entry.cancelledDate ?? null,
    })),
    billRate: worker.billRate || 0,
    billRateUnit: worker.billRateUnitCode || '',
    reportingBillRate: worker.reportingBillRate ?? null,
    payRate: 0,
    payRateUnit: '',
    reportingPayRate: 0,
    paySchedule: '',
    paymentCurrencyCode: worker.paymentCurrencyCode || null,
    legacySyncStatus: workerStatus,
    legacySyncError: workerSyncError,
    isRemoved: isRemoved,
    isNew: isWorkerNew(workerId, previousWorkerIds),
    firstDayOfWork: worker.firstDayOfWork ? parseDateOnly(worker.firstDayOfWork) : null,
    lastDayOfWork: worker.lastDayOfWork ? parseDateOnly(worker.lastDayOfWork) : null,
    // Termination signal — present only when the removal was an actual termination. Drives the
    // Terminated vs Removed badge in WorkerCard and is round-tripped back on save.
    terminationTypeId: ('terminationTypeId' in worker ? worker.terminationTypeId : null) ?? null,
    terminationReasonIds: ('terminationReasonIds' in worker ? worker.terminationReasonIds : null) ?? null
  }
  
  // Handle T4 vs Incorporated pay structures
  if (worker.t4SalariedPay) {
    formWorker.reportingPayRate = worker.t4SalariedPay.reportingPayRate || 0
  } else if (worker.timeBasedPay) {
    formWorker.payRate = worker.timeBasedPay.payRate || 0
    formWorker.payRateUnit = worker.timeBasedPay.payRateUnitCode || ''
    formWorker.paySchedule = worker.timeBasedPay.payScheduleCode || ''
  }

  // A worker saved before the rate list existed has none, so the flat fields stand in as their one rate.
  // No backfill: this lifting is what every stored engagement relies on.
  formWorker.payRates = worker.payRates?.length
    ? worker.payRates.map(rate => ({
        profileRateId: rate.profileRateId ?? null,
        rateTypeCode: rate.rateTypeCode ?? null,
        payRate: rate.payRate ?? null,
        payRateUnitCode: rate.payRateUnitCode ?? null,
        description: rate.description ?? null,
        scheduledPayRate: rate.scheduledPayRate ?? null,
        scheduledPayRateEffectiveDate: rate.scheduledPayRateEffectiveDate ?? null,
        // Read from the stored rate rather than defaulted, so the dialog shows what this placement was
        // actually written with. It is refreshed from the profile whenever a rate is reselected.
        billable: rate.billable ?? true,
      }))
    : liftedRateList(worker)
  
  // Scheduled rate fields are at worker level (like jobTitle), not inside timeBasedPay
  formWorker.scheduledPayRate = worker.scheduledPayRate || null
  // scheduledPayRateEffectiveDate is the canonical date for all scheduled rate changes
  formWorker.scheduledPayRateEffectiveDate = worker.scheduledPayRateEffectiveDate || worker.scheduledReportingPayRateEffectiveDate || null
  formWorker.scheduledReportingPayRate = worker.scheduledReportingPayRate || null
  formWorker.scheduledReportingPayRateEffectiveDate = worker.scheduledReportingPayRateEffectiveDate || null

  // Extension fields — persisted on the engagement worker document and updated via profile extension approval
  formWorker.originalStartDate = worker.originalStartDate || null
  formWorker.extensionStartDate = worker.extensionStartDate || null
  formWorker.extensionEndDate = worker.extensionEndDate || null

  return formWorker
}

/**
 * Converts form state to JSON Patch operations for saving
 * Uses the save endpoint: PATCH /api/engagement/{recordId}/versions/{version}/save
 * Includes ALL current form fields regardless of whether they were edited
 * 
 * ID Rule: Form stores 0 for unselected IDs, but database expects null
 * Use || null pattern to convert 0 to null for all ID fields
 */
export function formStateToSavePatchOperations(formState: FormState): PatchOperation[] {
  const operations: PatchOperation[] = []

  // Core Information Updates
  operations.push(
    { op: 'replace', path: '/coreInfo/name', value: emptyToNull(formState.coreData.engagementName) },
    { op: 'replace', path: '/coreInfo/code', value: emptyToNull(formState.coreData.engagementCode) },
    { op: 'replace', path: '/coreInfo/engagementTypeCode', value: emptyToNull(formState.coreData.engagementType) },
    // requestTypeCode: only send if engagementType is Advisory, otherwise null
    { op: 'replace', path: '/coreInfo/requestTypeCode', value: formState.coreData.engagementType === ENGAGEMENT_TYPE.ADVISORY ? emptyToNull(formState.coreData.requestType) : null },
    { op: 'replace', path: '/coreInfo/billingTypeCode', value: emptyToNull(formState.billingData.billingType) }
  )

  // Handle engagementSourceId for change requests - only send if engagementType is Advisory AND requestType is Change
  operations.push(
    { op: 'replace', path: '/coreInfo/engagementSourceId',
      value: (formState.coreData.engagementType === ENGAGEMENT_TYPE.ADVISORY && formState.coreData.requestType === ENGAGEMENT_REQUEST_TYPE.CHANGE) 
        ? unselectedIdToNull(formState.coreData.selectedOriginalEngagement)
        : null 
    }
  )

  // Additional core fields
  operations.push(
    { op: 'replace', path: '/coreInfo/internalCompanyOrganizationId', value: unselectedIdToNull(formState.coreData.selectedInternalCompany) },
    { op: 'replace', path: '/coreInfo/branchCode', value: emptyToNull(formState.coreData.selectedBranch) },
    // salesPersonUserProfileId: only send if engagementType is Advisory, otherwise null
    { op: 'replace', path: '/coreInfo/salesPersonUserProfileId', value: formState.coreData.engagementType === ENGAGEMENT_TYPE.ADVISORY ? unselectedIdToNull(formState.coreData.selectedSalesPerson) : null },
    { op: 'replace', path: '/coreInfo/engagementLeadUserProfileId', value: unselectedIdToNull(formState.coreData.selectedEngagementLead) },
    { op: 'replace', path: '/coreInfo/projectLeadUserProfileId', value: unselectedIdToNull(formState.coreData.selectedProjectLead) },
    // clientExecutiveUserProfileId: only send if engagementType is Advisory, otherwise null
    { op: 'replace', path: '/coreInfo/clientExecutiveUserProfileId', value: formState.coreData.engagementType === ENGAGEMENT_TYPE.ADVISORY ? unselectedIdToNull(formState.coreData.selectedClientExecutive) : null },
    { op: 'replace', path: '/coreInfo/currencyCode', value: emptyToNull(formState.billingData.selectedCurrency) },
    { op: 'replace', path: '/coreInfo/salesTaxSubdivisionCode', value: emptyToNull(formState.billingData.selectedSalesTax) },
    { op: 'replace', path: '/coreInfo/chargeSalesTax', value: formState.billingData.chargeSalesTax },
    // totalAmount: only send if engagementType is not Internal, otherwise null
    { op: 'replace', path: '/coreInfo/totalAmount', value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? (formState.billingData.totalAmount || null) : null },
    { op: 'replace', path: '/coreInfo/totalInternalBudget', value: formState.billingData.totalInternalBudget || null }
  )

  // Dates (convert to YYYY-MM-DD format, timezone-safe)
  // For internal engagements, if endDate is not provided, default to 3 years from startDate
  let endDateValue = formState.coreData.endDate
  if (!endDateValue && 
      formState.coreData.engagementType === ENGAGEMENT_TYPE.INTERNAL && 
      formState.coreData.startDate) {
    const threeYearsLater = new Date(formState.coreData.startDate)
    threeYearsLater.setFullYear(threeYearsLater.getFullYear() + 3)
    endDateValue = threeYearsLater
  }

  operations.push(
    { op: 'replace', path: '/coreInfo/startDate', value: formState.coreData.startDate ? formatDateOnly(formState.coreData.startDate) : null },
    { op: 'replace', path: '/coreInfo/endDate', value: endDateValue ? formatDateOnly(endDateValue) : null },
    { op: 'replace', path: '/coreInfo/agreementSignedDate', value: formState.coreData.agreementSignedDate ? formatDateOnly(formState.coreData.agreementSignedDate) : null },
    { op: 'replace', path: '/coreInfo/isPreEngagement', value: formState.coreData.isPreEngagement === true },
    { op: 'replace', path: '/coreInfo/closedDate', value: formState.coreData.closedDate ? formatDateOnly(formState.coreData.closedDate) : null },
    { op: 'replace', path: '/coreInfo/closedReasonCode', value: formState.coreData.closedReason ? emptyToNull(formState.coreData.closedReason) : null }
  )

  // Client Information - only save if engagement type is NOT Internal
  operations.push(
    { op: 'replace', path: '/clientInfo/clientOrganizationId', 
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? unselectedIdToNull(formState.coreData.selectedClient) : null },
    { op: 'replace', path: '/clientInfo/clientContactUserProfileId', 
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? unselectedIdToNull(formState.coreData.selectedContact) : null }
  )

  // Workers - split combined array into active and removed workers
  const apiActiveWorkers = formState.workers
    .filter(worker => !worker.isRemoved)
    .map(worker => transformWorkerToApi(worker, formState.billingData.billingType, formState.coreData.engagementType, false))
  
  const apiRemovedWorkers = formState.workers
    .filter(worker => worker.isRemoved)
    .map(worker => transformWorkerToApi(worker, formState.billingData.billingType, formState.coreData.engagementType, true))
  
  operations.push({ op: 'replace', path: '/workers', value: apiActiveWorkers })
  operations.push({ op: 'replace', path: '/removedWorkers', value: apiRemovedWorkers })

  // Timesheet Information
  // Force Sequential approval when only 1 approver (API requirement)
  const approvalSequence = formState.timesheetData.approvers.length === 1 
    ? TIMESHEET_APPROVAL_TYPE.SEQUENTIAL 
    : emptyToNull(formState.timesheetData.selectedApprovalSequence)
    
  operations.push(
    { op: 'replace', path: '/timesheetInfo/timesheetCycleCode', value: emptyToNull(formState.timesheetData.selectedTimesheetCycle) },
    { op: 'replace', path: '/timesheetInfo/approvers', value: formState.timesheetData.approvers.map(approver => ({ 
      userProfileId: unselectedIdToNull(approver.userProfileId),
      sequence: approver.sequence 
    })) },
    { op: 'replace', path: '/timesheetInfo/timesheetApprovalFlowCode', value: approvalSequence }
  )

  // Expense Info operations (single approver only, methodology is either OnlineApproval or NoExpense)
  // Use 'add' operation to create /expenseInfo node when absent or update when present
  // Clear approvers when expenses are disabled (NoExpense) to avoid stale approvers
  const expenseMethodology = emptyToNull(formState.timesheetData.expenseMethodologyCode)
  const expenseApprovers = expenseMethodology === EXPENSE_METHODOLOGY.NO_EXPENSE || !expenseMethodology
    ? []
    : (formState.timesheetData.expenseApprovers || []).map(approver => ({ 
        userProfileId: unselectedIdToNull(approver.userProfileId),
        sequence: approver.sequence 
      }))
  
  operations.push(
    { op: 'add', path: '/expenseInfo', value: {
      expenseMethodologyCode: expenseMethodology,
      approvers: expenseApprovers
    }}
  )

  // Projects/Tasks - replace entire array
  const isMilestoneBilling = formState.billingData.billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE
  const apiProjects = formState.tasks.map(task => ({
    id: task.id,
    taskName: emptyToNull(task.name),
    billable: task.billable,
    inactive: task.inactive,
    ...(isMilestoneBilling && { isUtilized: task.isUtilized ?? true }),
  }))
  operations.push({ op: 'replace', path: '/projects', value: apiProjects })

  // Invoice Information - only save if engagement type is NOT Internal
  operations.push(
    { op: 'replace', path: '/invoiceInfo/alternateBillCode',
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? emptyToNull(formState.invoiceData.selectedAlternateBill) : null },
    // Billing frequency: only send if not Internal AND not Milestone billing
    { op: 'replace', path: '/invoiceInfo/billingFrequencyCode', 
      value: (formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL && formState.billingData.billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE) 
        ? emptyToNull(formState.invoiceData.selectedBillingFrequency)
        : null },
    { op: 'replace', path: '/invoiceInfo/includeTimesheet', 
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? formState.invoiceData.includeTimesheet : null },
    { op: 'replace', path: '/invoiceInfo/paymentTermsCode', 
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? emptyToNull(formState.invoiceData.selectedPaymentTerms) : null },
    { op: 'replace', path: '/invoiceInfo/invoiceTemplateCode', 
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? emptyToNull(formState.invoiceData.selectedInvoiceTemplate) : null },
    { op: 'replace', path: '/invoiceInfo/invoiceRecipientUserProfileId', 
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? unselectedIdToNull(formState.invoiceData.selectedInvoiceRecipient) : null },
    { op: 'replace', path: '/invoiceInfo/invoiceDeliveryMethodCode', 
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL ? emptyToNull(formState.invoiceData.selectedDeliveryMethod) : null },
    { op: 'replace', path: '/invoiceInfo/invoiceNotes',
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL 
        ? formState.invoiceData.invoiceNotes.map(note => emptyToNull(note.value))
        : [] },
    { op: 'replace', path: '/invoiceInfo/invoiceCcEmails',
      value: formState.coreData.engagementType !== ENGAGEMENT_TYPE.INTERNAL
        ? (formState.invoiceData.selectedDeliveryMethod === 'SoftCopy' ? formState.invoiceData.ccEmails : [])
        : [] }
  )

  // Milestones (for milestone-based billing) - always send value to clear when not milestone billing
  const apiMilestones = (formState.billingData.billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE && formState.milestones.length > 0) 
    ? formState.milestones.map(milestone => ({
        startDate: milestone.startDate ? formatDateOnly(milestone.startDate) : null,
        endDate: milestone.endDate ? formatDateOnly(milestone.endDate) : null,
        statusCode: emptyToNull(milestone.status),
        milestoneReference: emptyToNull(milestone.reference),
        description: emptyToNull(milestone.description),
        amount: milestone.amount,
        internalBudget: milestone.internalBudget
      }))
    : []
  operations.push({ op: 'replace', path: '/milestones', value: apiMilestones })

  // Revenue Recognition (for milestone-based billing) - transform form Record to API array
  // Always send value to clear when not milestone billing
  const apiRevenueRecognition = formState.billingData.billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE 
    ? Object.entries(formState.revenueRecognitionData.periods)
        .filter(([_, periodData]) => periodData.amount !== null && periodData.amount !== undefined)
        .map(([startDate, periodData]) => ({
          startDate,
          amount: periodData.amount as number,
          isLocked: periodData.isLocked ?? false
        }))
        .sort((a, b) => a.startDate.localeCompare(b.startDate)) // Sort by date for consistency
    : []
  operations.push({ op: 'replace', path: '/revenueRecognition/periods', value: apiRevenueRecognition })

  return operations
}

/**
 * Transforms a form worker to API worker format
 */
export function transformWorkerToApi(worker: WorkerFormData, billingType?: string, engagementType?: string, isRemoved: boolean = false): Worker | RemovedWorker {
  // billRate and billRateUnitCode: only send if billing type is not milestone AND engagement type is not internal, otherwise null
  const hasBillRate = billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE && engagementType !== ENGAGEMENT_TYPE.INTERNAL
  const hasReportingBillRate = billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE

  const baseApiWorker = {
    workerUserProfileId: unselectedIdToNull(worker.workerUserProfileId),
    firstDayOfWork: worker.firstDayOfWork ? formatDateOnly(worker.firstDayOfWork) : null,
    // Per-worker end date; sent for both (active: winds down early; removed: termination date).
    lastDayOfWork: worker.lastDayOfWork ? formatDateOnly(worker.lastDayOfWork) : null,
    jobCategoryCode: emptyToNull(worker.jobCategoryCode),
    jobFunctionCode: emptyToNull(worker.jobFunctionCode),
    jobTitle: emptyToNull(worker.jobTitle),
    lineOfBusinessCode: emptyToNull(worker.lineOfBusinessCode),
    budgetedHoursPerDay: worker.budgetedHours,
    // The whole list goes back, applied and cancelled entries included; the service keeps their stamps and
    // places as stored. A new entry carries no id, since the service reads a missing one as new.
    scheduledBudgetedHours: (worker.scheduledBudgetedHours ?? []).map(entry => ({
      ...(entry.id ? { id: entry.id } : {}),
      effectiveDate: entry.effectiveDate,
      hoursPerDay: entry.hoursPerDay,
      appliedDate: entry.appliedDate ?? null,
      cancelledDate: entry.cancelledDate ?? null,
    })),
    billRate: hasBillRate ? worker.billRate : null,
    billRateUnitCode: hasBillRate ? emptyToNull(worker.billRateUnit) : null,
    reportingBillRate: hasReportingBillRate ? worker.reportingBillRate : null,
    paymentCurrencyCode: emptyToNull(worker.paymentCurrencyCode)
  }

  // Create the appropriate worker type based on whether it's removed. Removed workers carry their
  // last day of work plus any termination signal (type/reasons), so a form save round-trips the
  // Terminated-vs-Removed distinction instead of stripping it.
  const apiWorker: Worker | RemovedWorker = isRemoved
    ? {
        ...baseApiWorker, // includes lastDayOfWork (termination date)
        terminationTypeId: worker.terminationTypeId ?? null,
        terminationReasonIds: worker.terminationReasonIds ?? null
      }
    : baseApiWorker

  // Handle T4 vs Incorporated pay structures
  if (worker.type === WORKER_TYPE.T4_SALARIED) {
    apiWorker.t4SalariedPay = {
      reportingPayRate: worker.reportingPayRate
    }
    apiWorker.timeBasedPay = null
  } else {
    apiWorker.t4SalariedPay = null
    apiWorker.timeBasedPay = {
      payRate: worker.payRate,
      payRateUnitCode: emptyToNull(worker.payRateUnit),
      payScheduleCode: emptyToNull(worker.paySchedule)
    }
  }

  // Only an hourly worker has a rate list. A salaried one is paid a reporting rate and the slots do not
  // apply, so the collection is cleared rather than carried across the conversion.
  apiWorker.payRates = worker.type === WORKER_TYPE.T4_SALARIED
    ? null
    : worker.payRates.map(rate => ({
        profileRateId: rate.profileRateId || null,
        rateTypeCode: emptyToNull(rate.rateTypeCode ?? undefined),
        payRate: rate.payRate ?? null,
        payRateUnitCode: emptyToNull(rate.payRateUnitCode ?? undefined),
        description: emptyToNull(rate.description ?? undefined),
        scheduledPayRate: rate.scheduledPayRate ?? null,
        scheduledPayRateEffectiveDate: rate.scheduledPayRateEffectiveDate || null,
        // Sent as the profile set it, and never overridden here: billability belongs to the rate, so
        // the same rate reads the same way on every placement. Defaulted rather than left undefined,
        // since Billable is a non-nullable bool on the receiving model and an absent key deserializes
        // as false, silently stopping the rate being invoiced.
        billable: rate.billable ?? true,
      }))
  
  // Scheduled rate fields are at worker level (like jobTitle), not inside timeBasedPay
  apiWorker.scheduledPayRate = worker.scheduledPayRate || null
  apiWorker.scheduledPayRateEffectiveDate = worker.scheduledPayRateEffectiveDate || null
  apiWorker.scheduledReportingPayRate = worker.scheduledReportingPayRate || null
  apiWorker.scheduledReportingPayRateEffectiveDate = worker.scheduledReportingPayRate ? (worker.scheduledPayRateEffectiveDate || null) : null

  // Extension fields — persisted so the WorkerCard always shows the Extended badge after an extension is approved
  apiWorker.originalStartDate = worker.originalStartDate || null
  apiWorker.extensionStartDate = worker.extensionStartDate || null
  apiWorker.extensionEndDate = worker.extensionEndDate || null

  return apiWorker
}
